import { FormattedText, ReactPortalComp } from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import {
  LandingButton,
  LandingButtonTertiary,
} from "@tm/ui-widgets/components";
import { useCallback } from "react";

import { ModalWrapper } from "../../../components/ModalWrapper/ModalWrapper";

import type {
  CancelPolicyFooterAction,
  CancelPolicySubmitConfirmationPopup as CancelPolicySubmitConfirmationPopupData,
} from "../types";

import {
  descriptionStyle,
  LoadingOverlay,
  PopupActions,
  PopupBody,
  PopupHeader,
  SheetBackdrop,
  SheetBody,
  SheetPanel,
  Spinner,
  titleStyle,
} from "./CancelPolicySubmitConfirmationPopup.style";

export interface CancelPolicySubmitConfirmationPopupProps
  extends CancelPolicySubmitConfirmationPopupData {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const CancelPolicySubmitConfirmationPopup = ({
  title,
  description,
  actionList,
  visible,
  onClose,
  onConfirm,
  isLoading,
}: CancelPolicySubmitConfirmationPopupProps) => {
  const isMobile = useBreakpointV2() === "sm";

  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) handleClose();
    },
    [handleClose]
  );

  const handleAction = useCallback(
    (action: CancelPolicyFooterAction) => {
      if (isLoading) return;
      switch (action.actionId) {
        case "cancelBooking":
          onConfirm();
          break;
        case "dismissPopup":
          onClose();
          break;
        default:
          break;
      }
    },
    [isLoading, onConfirm, onClose]
  );

  if (!visible) return null;

  const body = (
    <>
      <PopupHeader>
        {title && <FormattedText value={title} baseStyle={titleStyle} />}
        {description && (
          <FormattedText value={description} baseStyle={descriptionStyle} />
        )}
      </PopupHeader>

      {actionList && actionList.length > 0 && (
        <PopupActions>
          {actionList.map((action, index) => {
            const label = action.actionTitle ?? action.label;
            if (!label || typeof label !== "string") return null;

            return action.isPrimary ? (
              <LandingButton
                key={action.actionId ?? `action-${index}`}
                customTitle={label}
                customWidth="100%"
                customHeight="48px"
                disableState={action.disabled === true || isLoading}
                isLoading={isLoading}
                onClick={() => handleAction(action)}
              />
            ) : (
              <LandingButtonTertiary
                key={action.actionId ?? `action-${index}`}
                title={label}
                customWidth="100%"
                customHeight="48px"
                disableState={action.disabled === true || isLoading}
                onClick={() => handleAction(action)}
              />
            );
          })}
        </PopupActions>
      )}

      {isLoading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
    </>
  );

  if (isMobile) {
    return (
      <ReactPortalComp>
        <SheetBackdrop onClick={handleBackdropClick}>
          <SheetPanel role="dialog" aria-modal="true">
            <SheetBody>{body}</SheetBody>
          </SheetPanel>
        </SheetBackdrop>
      </ReactPortalComp>
    );
  }

  return (
    <ModalWrapper isOpen={visible} onClose={handleClose} customWidth="420px">
      <PopupBody>{body}</PopupBody>
    </ModalWrapper>
  );
};

export default CancelPolicySubmitConfirmationPopup;
